/**
 * Dev-only mock active cycle. Drives the in-care preview on the home
 * page so designers can see what an active cycle home looks like
 * without having a real cycle row in the backend.
 *
 * Enabled with `?dev_force_active_cycle=true` on a `__DEV__` build.
 * Specific review states can be selected with `?dev_active_cycle_scenario=...`.
 * Persisted in sessionStorage for the tab. Same pattern as the other
 * dev overrides.
 *
 * The mock describes an egg-freezing cycle that started 10 days ago,
 * currently mid-stimulation (day 7 of protocol). Other consumers that
 * read cycle data (CareScreen, CycleDetailScreen, TreatmentDashboard,
 * ActiveCycleHero) all benefit from the same mock since it short-
 * circuits the two underlying hooks (`useActiveCycle`,
 * `useCyclePhases`).
 */
import type { Cycle, Phase } from "../hooks/useCycles";

const STORAGE_KEY = "__moon_dev_force_active_cycle";
const SCENARIO_STORAGE_KEY = "__moon_dev_active_cycle_scenario";

export type DevActiveCycleScenario =
  | "default"
  | "missing_where_when"
  // Egg retrieval started two days ago and is still awaiting its recorded
  // result; this is the Cycle Detail reproduction for issue #1557.
  | "retrieval_pending"
  // Post-transfer two-week wait (beta pending). Produces an IVF cycle whose
  // stim through transfer steps are complete and whose luteal phase is open, so
  // the homepage/care hero resolves to the beta-wait state instead of stim.
  | "waiting_beta"
  // Post-transfer wait with no beta date yet; exercises the scheduling CTA.
  | "waiting_beta_pending"
  // Cycle Detail two-week-wait layout fixture (from #1951).
  | "waiting_phase";

export type DevCycleDetailOutcome =
  "pregnancy" | "no_pregnancy" | "miscarriage" | "cancelled" | "other";

type DevOutcomeSource = "provider" | "patient" | "partner" | "system";
type DevOutcomeVerification = "unverified" | "verified" | "refuted";
type DevOutcomeResult = "pregnancy" | "no_pregnancy";
type DevOutcomeRecordStatus = "current" | "corrected" | "entered_in_error";

/**
 * Dev-only post-cycle fixture for the Cycle Detail close-out dialog.
 *
 * The route uses this independently from the active-cycle fixture so the
 * post-cycle handoff can be reviewed on the real product surface without
 * requiring a closed cycle in a test account.
 */
export const DEV_CYCLE_DETAIL_OUTCOME_OVERRIDE: DevCycleDetailOutcome | null =
  (() => {
    if (!__DEV__ || typeof window === "undefined") return null;
    const outcome = new URLSearchParams(window.location.search).get(
      "dev_cycle_detail_outcome",
    );
    if (
      outcome === "pregnancy" ||
      outcome === "no_pregnancy" ||
      outcome === "miscarriage" ||
      outcome === "cancelled" ||
      outcome === "other"
    ) {
      return outcome;
    }
    return null;
  })();

function isKnownScenario(
  value: string | null | undefined,
): value is Exclude<DevActiveCycleScenario, "default"> {
  return (
    value === "missing_where_when" ||
    value === "retrieval_pending" ||
    value === "waiting_beta" ||
    value === "waiting_beta_pending" ||
    value === "waiting_phase"
  );
}

function getDevActiveCycleScenario(): DevActiveCycleScenario {
  if (!__DEV__ || typeof window === "undefined") return "default";
  const fromUrl = new URLSearchParams(window.location.search).get(
    "dev_active_cycle_scenario",
  );
  if (isKnownScenario(fromUrl)) {
    try {
      window.sessionStorage?.setItem(SCENARIO_STORAGE_KEY, fromUrl);
    } catch {
      /* sessionStorage may be disabled */
    }
    return fromUrl;
  }
  if (fromUrl === "default") {
    try {
      window.sessionStorage?.removeItem(SCENARIO_STORAGE_KEY);
    } catch {
      /* sessionStorage may be disabled */
    }
    return "default";
  }
  try {
    const stored = window.sessionStorage?.getItem(SCENARIO_STORAGE_KEY);
    return isKnownScenario(stored) ? stored : "default";
  } catch {
    return "default";
  }
}

export const DEV_ACTIVE_CYCLE_OVERRIDE: boolean = (() => {
  if (!__DEV__ || typeof window === "undefined") return false;
  const fromUrl = new URLSearchParams(window.location.search).get(
    "dev_force_active_cycle",
  );
  if (fromUrl === "true" || fromUrl === "1") {
    try {
      window.sessionStorage?.setItem(STORAGE_KEY, "1");
    } catch {
      /* sessionStorage may be disabled */
    }
    return true;
  }
  if (fromUrl === "false" || fromUrl === "0") {
    try {
      window.sessionStorage?.removeItem(STORAGE_KEY);
    } catch {
      /* sessionStorage may be disabled */
    }
    return false;
  }
  try {
    return window.sessionStorage?.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
})();

const DEV_CARE_CONNECTIONS_CYCLE = (() => {
  if (!__DEV__ || typeof window === "undefined") return false;
  return (
    new URLSearchParams(window.location.search).get(
      "dev_care_connections_cycle",
    ) === "1"
  );
})();

/**
 * An explicit false value is useful for authenticated visual routes that need
 * to exercise the no-cycle surface even when the provisioned review account
 * has an open cycle in the API.
 */
export const DEV_FORCE_NO_ACTIVE_CYCLE: boolean = (() => {
  if (!__DEV__ || typeof window === "undefined") return false;
  const value = new URLSearchParams(window.location.search).get(
    "dev_force_active_cycle",
  );
  return value === "false" || value === "0";
})();

export const DEV_ACTIVE_CYCLE_SCENARIO = getDevActiveCycleScenario();

/**
 * Optional beta-wait day override for route-level visual review.
 * `?dev_wait_day=1|8|12` uses a fourteen-day beta window while moving the
 * transfer date so the Home reassurance thresholds can be reviewed.
 */
export const DEV_WAIT_DAYS_AFTER_TRANSFER_OVERRIDE: number | null = (() => {
  if (!__DEV__ || typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("dev_wait_day");
  if (!raw) return null;
  const days = Number(raw);
  return Number.isInteger(days) && days >= 0 && days <= 14 ? days : null;
})();

const CYCLE_TYPE_STORAGE_KEY = "__moon_dev_cycle_type";

const DEV_CYCLE_TYPES = [
  "ivf",
  "icsi",
  "iui",
  "fet",
  "oi",
  "egg_freezing",
  "embryo_freezing",
  "sperm_freezing",
] as const;

export type DevCycleTypeOverride = (typeof DEV_CYCLE_TYPES)[number];

function isDevCycleType(value: string | null): value is DevCycleTypeOverride {
  return !!value && (DEV_CYCLE_TYPES as readonly string[]).includes(value);
}

/**
 * Optional `?dev_cycleType=` override so the treatment hub can be reviewed for
 * any cycle type (the mock cycle is otherwise egg-freezing). It only reshapes
 * the step list / copy via `getStepsForPerspective`; the mock phases stay
 * generic. Persisted in sessionStorage for the tab like the scenario.
 */
function getDevCycleTypeOverride(): DevCycleTypeOverride | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  const fromUrl = new URLSearchParams(window.location.search).get(
    "dev_cycleType",
  );
  if (isDevCycleType(fromUrl)) {
    try {
      window.sessionStorage?.setItem(CYCLE_TYPE_STORAGE_KEY, fromUrl);
    } catch {
      /* sessionStorage may be disabled */
    }
    return fromUrl;
  }
  if (fromUrl === "default" || fromUrl === "reset") {
    try {
      window.sessionStorage?.removeItem(CYCLE_TYPE_STORAGE_KEY);
    } catch {
      /* sessionStorage may be disabled */
    }
    return null;
  }
  try {
    const stored = window.sessionStorage?.getItem(CYCLE_TYPE_STORAGE_KEY);
    return isDevCycleType(stored) ? stored : null;
  } catch {
    return null;
  }
}

export const DEV_CYCLE_TYPE_OVERRIDE = getDevCycleTypeOverride();

function getDevOutcomeField<T extends string>(
  key: string,
  values: readonly T[],
): T | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  const value = new URLSearchParams(window.location.search).get(key);
  return value && (values as readonly string[]).includes(value)
    ? (value as T)
    : null;
}

/** Optional provenance fields for a deterministic post-result Home fixture. */
export const DEV_OUTCOME_SOURCE_OVERRIDE: DevOutcomeSource | null =
  getDevOutcomeField("dev_outcome_source", [
    "provider",
    "patient",
    "partner",
    "system",
  ] as const);
export const DEV_OUTCOME_VERIFICATION_OVERRIDE: DevOutcomeVerification | null =
  getDevOutcomeField("dev_outcome_verification", [
    "unverified",
    "verified",
    "refuted",
  ] as const);
/**
 * `?dev_pregnancy_stands=continuing|ended|birth` and `?dev_pregnancy_weeks=8`
 * put the fixture outcome into a state a patient could only otherwise reach by
 * saving against a real cycle.
 *
 * The card only says "still pregnant" once she has given a week — nothing but the
 * update step writes one — so without these the reviewable states are the dialog
 * and nothing else. Read-only fixtures, exactly like the provenance overrides
 * above; they change what the mock outcome carries, never what a write does.
 */
export const DEV_PREGNANCY_STANDS_OVERRIDE:
  "continuing" | "ended" | "birth" | null = getDevOutcomeField(
  "dev_pregnancy_stands",
  ["continuing", "ended", "birth"] as const,
);

export const DEV_PREGNANCY_WEEKS_OVERRIDE: number | null = (() => {
  if (!__DEV__ || typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get(
    "dev_pregnancy_weeks",
  );
  const parsed = raw ? Number.parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 45 ? parsed : null;
})();

/**
 * `?dev_pregnancy_babies=2` — how many babies the recorded birth says.
 *
 * A birth reads differently in the plural, and the fixture had no way to say so:
 * it forced `live_birth` with no count, so the plural copy had no reviewable
 * state. The bounds are the update step's own (`MoonNumericField`, 1 to 10), so
 * the fixture cannot describe a birth the product could not record.
 */
export const DEV_PREGNANCY_BABIES_OVERRIDE: number | null = (() => {
  if (!__DEV__ || typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get(
    "dev_pregnancy_babies",
  );
  const parsed = raw ? Number.parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) && parsed >= 1 && parsed <= 10 ? parsed : null;
})();

export const DEV_OUTCOME_RESULT_OVERRIDE: DevOutcomeResult | null =
  getDevOutcomeField("dev_outcome_result", [
    "pregnancy",
    "no_pregnancy",
  ] as const);
/**
 * `?dev_outcome_record_status=entered_in_error` retracts the fixture's result.
 *
 * A retracted row was entered by mistake, so Home must describe it as unsettled
 * rather than as the result it names. Nothing else can reach that state: the app
 * has no write path for this field, so without a knob the one surface that has
 * to get it right had nowhere to be reviewed.
 */
export const DEV_OUTCOME_RECORD_STATUS_OVERRIDE: DevOutcomeRecordStatus | null =
  getDevOutcomeField("dev_outcome_record_status", [
    "current",
    "corrected",
    "entered_in_error",
  ] as const);

/**
 * `?dev_cycle_outcome_acknowledged=true|false` stamps `acknowledgedAt` on the
 * closed-cycle fixture's outcome.
 *
 * This is the field the arrival carousel and the acknowledged pause divide on, so
 * it is the one param that shows the real transition. Unlike `dev_outcome_stage`
 * it does not short-circuit the stage hook — the state is still derived from the
 * outcome record, so it exercises the production read path rather than bypassing
 * it.
 */
export const DEV_CYCLE_OUTCOME_ACKNOWLEDGED_OVERRIDE: boolean | null = (() => {
  const value = getDevOutcomeField("dev_cycle_outcome_acknowledged", [
    "true",
    "false",
  ] as const);
  return value === null ? null : value === "true";
})();

/**
 * `?dev_cycle_ended_days_ago=60` ages the closed-cycle fixture's ending.
 *
 * The fixture ends one day ago, which is inside every recency window, so the
 * question that matters for a pregnancy — is this still Home's subject once the
 * cycle is long over — had no reachable state to review. A pregnancy lasts
 * months; its Home has to be reviewable months in.
 */
export const DEV_CYCLE_ENDED_DAYS_AGO_OVERRIDE: number | null = (() => {
  if (!__DEV__ || typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get(
    "dev_cycle_ended_days_ago",
  );
  if (!raw) return null;
  const days = Number(raw);
  return Number.isInteger(days) && days >= 0 && days <= 3_650 ? days : null;
})();

export type DevStepState = "completed" | "scheduled" | "skipped" | "outcome";

/**
 * `?dev_stepStates=egg_retrieval:scheduled,pre_treatment:completed` forces
 * per-step panel states so the completed / skipped / scheduled UI can be
 * reviewed without a real cycle in each state. `outcome` leaves egg retrieval
 * pending but gives it a today-date procedure so the outcome handoff can be
 * reviewed too. Dev-only; not persisted (pass it on the cycle-detail URL).
 */
function getDevStepStatesOverride(): Record<string, DevStepState> | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("dev_stepStates");
  if (!raw) return null;
  const map: Record<string, DevStepState> = {};
  for (const pair of raw.split(",")) {
    const [id, state] = pair.split(":").map((s) => s.trim());
    if (
      id &&
      (state === "completed" ||
        state === "scheduled" ||
        state === "skipped" ||
        state === "outcome")
    ) {
      map[id] = state;
    }
  }
  return Object.keys(map).length > 0 ? map : null;
}

export const DEV_STEP_STATES_OVERRIDE = getDevStepStatesOverride();

function daysAgoISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

const MOCK_CYCLE_ID = "00000000-0000-0000-0000-00000000c001";
const MOCK_PATIENT_ID = "00000000-0000-0000-0000-0000000000a1";
const MOCK_PRE_TREATMENT_PHASE_ID = "00000000-0000-0000-0000-00000000a001";
const MOCK_PROTOCOL_PHASE_ID = "00000000-0000-0000-0000-00000000a002";
const MOCK_MONITORING_DATE = daysAgoISO(1);

/** Egg-freezing cycle, open, started 10 days ago. */
export const MOCK_ACTIVE_CYCLE: Cycle = {
  id: MOCK_CYCLE_ID,
  patientId: MOCK_PATIENT_ID,
  acceptedTreatmentPlanId: null,
  acceptedTreatmentPlanVersion: null,
  partnerId: null,
  relationshipId: null,
  description: null,
  cycleType: "egg_freezing",
  protocol: "gnrh_antagonist",
  menstruationStartDate: daysAgoISO(10),
  startDate: daysAgoISO(10),
  endDate: null,
  calculatedFertileWindowStart: null,
  calculatedFertileWindowEnd: null,
  status: "open",
  providerId: null,
  ownership: "patient",
  ownerId: MOCK_PATIENT_ID,
  sourceId: null,
  audience: [],
  createdAt: daysAgoISO(10),
  updatedAt: daysAgoISO(0),
  updatedBy: null,
  source: "patient",
  verificationStatus: "unverified",
  verifiedAt: null,
  verifiedBy: null,
  verificationNotes: null,
  rawCycleType: null,
  rawProtocol: null,
  provider: null,
} as Cycle;

export const MOCK_ACTIVE_CYCLE_MISSING_WHERE_WHEN: Cycle = {
  ...MOCK_ACTIVE_CYCLE,
  clinic: null,
  startDate: null,
  menstruationStartDate: null,
} as Cycle;

/** IVF cycle in the two-week-wait state for date-field route QA. */
export const MOCK_WAITING_ACTIVE_CYCLE: Cycle = {
  ...MOCK_ACTIVE_CYCLE,
  cycleType: "ivf",
  startDate: daysAgoISO(20),
  menstruationStartDate: daysAgoISO(20),
} as Cycle;

/** A closed cycle used only by the deterministic post-cycle route fixture. */
export const MOCK_CLOSED_CYCLE: Cycle = {
  ...MOCK_ACTIVE_CYCLE,
  id: "00000000-0000-0000-0000-00000000c002",
  cycleType: DEV_CYCLE_TYPE_OVERRIDE ?? "egg_freezing",
  endDate: daysAgoISO(DEV_CYCLE_ENDED_DAYS_AGO_OVERRIDE ?? 1),
  status: "closed",
  updatedAt: daysAgoISO(0),
  outcome: {
    // Without an outcome id `readCycleEnding` reports `outcomeId: null`, so
    // `shouldUseCycleEndingForHomeExperience` refuses the ending and no
    // post-result Home is reachable from this fixture at all.
    id: "00000000-0000-0000-0000-00000000d002",
    type: DEV_CYCLE_DETAIL_OUTCOME_OVERRIDE ?? "pregnancy",
    resultStatus:
      DEV_OUTCOME_RESULT_OVERRIDE === "no_pregnancy" ||
      DEV_CYCLE_DETAIL_OUTCOME_OVERRIDE === "no_pregnancy"
        ? "verified_negative"
        : "verified_positive",
    source: DEV_OUTCOME_SOURCE_OVERRIDE ?? "patient",
    verificationStatus: DEV_OUTCOME_VERIFICATION_OVERRIDE ?? "verified",
    // Defaults to the standing record, which is what every existing route
    // already got from this field being absent.
    recordStatus: DEV_OUTCOME_RECORD_STATUS_OVERRIDE ?? "current",
    acknowledgedAt: DEV_CYCLE_OUTCOME_ACKNOWLEDGED_OVERRIDE
      ? daysAgoISO(0)
      : null,
    cancellationReason: null,
    pregnancyOutcome: null,
    miscarriageOutcome: null,
    biochemicalPregnancyObserved: null,
    // The result is dated with the ending it belongs to, so an aged fixture does
    // not describe a result recorded after the cycle it came from.
    dateRecorded: daysAgoISO(DEV_CYCLE_ENDED_DAYS_AGO_OVERRIDE ?? 1),
    resultDate: daysAgoISO(DEV_CYCLE_ENDED_DAYS_AGO_OVERRIDE ?? 1),
    recordedAt: daysAgoISO(DEV_CYCLE_ENDED_DAYS_AGO_OVERRIDE ?? 1),
    weekCount: null,
    liveBirthCount: null,
    notes: null,
  },
} as Cycle;

/**
 * IVF cycle, open, in the post-transfer two-week wait. Started ~30 days ago;
 * the transfer landed 5 days ago and the beta test is 5 days out. Used by the
 * `waiting_beta` scenario so the beta-wait homepage can be reviewed end to end.
 */
export const MOCK_WAITING_CYCLE: Cycle = {
  ...MOCK_ACTIVE_CYCLE,
  cycleType: "ivf",
  menstruationStartDate: daysAgoISO(30),
  startDate: daysAgoISO(30),
  createdAt: daysAgoISO(30),
} as Cycle;

const BASE_ACTIVE_CYCLE_REVIEW_MOCK =
  DEV_ACTIVE_CYCLE_SCENARIO === "waiting_phase"
    ? MOCK_WAITING_ACTIVE_CYCLE
    : DEV_ACTIVE_CYCLE_SCENARIO === "waiting_beta"
      ? MOCK_WAITING_CYCLE
      : DEV_ACTIVE_CYCLE_SCENARIO === "missing_where_when"
        ? MOCK_ACTIVE_CYCLE_MISSING_WHERE_WHEN
        : MOCK_ACTIVE_CYCLE;

// `?dev_cycleType=` reshapes the resolved mock so any cycle type's step list
// can be reviewed on cycle-detail. Only the cycleType changes; phases stay
// generic (enough to render the tabs/states, not full per-type data).
export const ACTIVE_CYCLE_REVIEW_MOCK: Cycle = DEV_CYCLE_TYPE_OVERRIDE
  ? ({
      ...BASE_ACTIVE_CYCLE_REVIEW_MOCK,
      cycleType: DEV_CYCLE_TYPE_OVERRIDE,
      ...(DEV_CARE_CONNECTIONS_CYCLE
        ? {
            providerId: "81000000-0000-4000-8000-000000000001",
            provider: "Northstar Fertility",
          }
        : {}),
      ...(DEV_OUTCOME_SOURCE_OVERRIDE || DEV_OUTCOME_VERIFICATION_OVERRIDE
        ? {
            outcome: {
              id: "00000000-0000-0000-0000-00000000c003",
              cycleId: MOCK_CYCLE_ID,
              type:
                DEV_PREGNANCY_STANDS_OVERRIDE === "ended"
                  ? "miscarriage"
                  : (DEV_OUTCOME_RESULT_OVERRIDE ?? "no_pregnancy"),
              resultStatus:
                DEV_OUTCOME_RESULT_OVERRIDE === "pregnancy"
                  ? "positive"
                  : "negative",
              cancellationReason: null,
              pregnancyOutcome:
                DEV_PREGNANCY_STANDS_OVERRIDE === "birth"
                  ? // The same reading the update step writes: more than one
                    // baby is the only thing the count can mean here.
                    DEV_PREGNANCY_BABIES_OVERRIDE &&
                    DEV_PREGNANCY_BABIES_OVERRIDE > 1
                    ? "multiple_live_birth"
                    : "live_birth"
                  : null,
              miscarriageOutcome:
                DEV_PREGNANCY_STANDS_OVERRIDE === "ended"
                  ? "complete_miscarriage"
                  : null,
              biochemicalPregnancyObserved: null,
              dateRecorded: daysAgoISO(1),
              weekCount: DEV_PREGNANCY_WEEKS_OVERRIDE,
              liveBirthCount:
                DEV_PREGNANCY_STANDS_OVERRIDE === "birth"
                  ? DEV_PREGNANCY_BABIES_OVERRIDE
                  : null,
              createdAt: daysAgoISO(1),
              updatedAt: daysAgoISO(0),
              updatedBy: null,
              source: DEV_OUTCOME_SOURCE_OVERRIDE ?? "patient",
              verificationStatus:
                DEV_OUTCOME_VERIFICATION_OVERRIDE ?? "unverified",
              verifiedAt: null,
              verifiedBy: null,
              verificationNotes: null,
              notes: null,
            },
          }
        : {}),
    } as Cycle)
  : ({
      ...BASE_ACTIVE_CYCLE_REVIEW_MOCK,
      ...(DEV_CARE_CONNECTIONS_CYCLE
        ? {
            providerId: "81000000-0000-4000-8000-000000000001",
            provider: "Northstar Fertility",
          }
        : {}),
    } as Cycle);

/** Two phases: pre-treatment completed, protocol (stim) active on day 7. */
const MOCK_DEFAULT_CYCLE_PHASES: Phase[] = [
  {
    id: MOCK_PRE_TREATMENT_PHASE_ID,
    cycleId: MOCK_CYCLE_ID,
    type: "pre_treatment",
    startDate: daysAgoISO(10),
    startTime: null,
    endDate: daysAgoISO(7),
    notes: null,
    createdAt: daysAgoISO(10),
    updatedAt: daysAgoISO(7),
    updatedBy: null,
    source: "patient",
    verificationStatus: "unverified",
    verifiedAt: null,
    verifiedBy: null,
    verificationNotes: null,
  } as Phase,
  {
    id: MOCK_PROTOCOL_PHASE_ID,
    cycleId: MOCK_CYCLE_ID,
    type: "protocol",
    startDate: daysAgoISO(6),
    startTime: null,
    endDate: null,
    notes: null,
    createdAt: daysAgoISO(6),
    updatedAt: daysAgoISO(0),
    updatedBy: null,
    source: "patient",
    verificationStatus: "unverified",
    verifiedAt: null,
    verifiedBy: null,
    verificationNotes: null,
    ovaryFollicles: [
      ...[8, 9, 10, 11, 12, 13].map((size, index) => ({
        id: `00000000-0000-0000-0000-00000000f0${index}`,
        patientId: MOCK_PATIENT_ID,
        phaseId: MOCK_PROTOCOL_PHASE_ID,
        ovarySide: index < 3 ? "left" : "right",
        date: MOCK_MONITORING_DATE,
        count: 1,
        size,
        clinicalAssessment: "normal",
        clinicalAssessmentDetails: null,
        createdAt: MOCK_MONITORING_DATE,
        updatedAt: MOCK_MONITORING_DATE,
        updatedBy: null,
        source: "provider",
        verificationStatus: "verified",
        verifiedAt: null,
        verifiedBy: null,
        verificationNotes: null,
      })),
      ...[14, 15, 15, 16, 17, 18].map((size, index) => ({
        id: `00000000-0000-0000-0000-00000000f1${index}`,
        patientId: MOCK_PATIENT_ID,
        phaseId: MOCK_PROTOCOL_PHASE_ID,
        ovarySide: index < 3 ? "left" : "right",
        date: MOCK_MONITORING_DATE,
        count: 1,
        size,
        clinicalAssessment: "normal",
        clinicalAssessmentDetails: null,
        createdAt: MOCK_MONITORING_DATE,
        updatedAt: MOCK_MONITORING_DATE,
        updatedBy: null,
        source: "provider",
        verificationStatus: "verified",
        verifiedAt: null,
        verifiedBy: null,
        verificationNotes: null,
      })),
    ],
    testResults: [
      {
        id: "00000000-0000-0000-0000-00000000e201",
        patientId: MOCK_PATIENT_ID,
        phaseId: MOCK_PROTOCOL_PHASE_ID,
        cycleId: MOCK_CYCLE_ID,
        name: "Estradiol",
        type: "blood",
        bloodTestCategory: "hormonal",
        doctorValue: "820",
        patientValue: null,
        value: "820",
        valueType: "numeric",
        unit: "pg/mL",
        date: MOCK_MONITORING_DATE,
        notes: null,
        clinicalAssessment: "normal",
        clinicalAssessmentDetails: null,
        createdAt: MOCK_MONITORING_DATE,
        updatedAt: MOCK_MONITORING_DATE,
        updatedBy: null,
        source: "provider",
        verificationStatus: "verified",
        verifiedAt: null,
        verifiedBy: null,
        verificationNotes: null,
      },
    ],
    fertilityMetrics: [
      {
        id: "00000000-0000-0000-0000-00000000ed01",
        phaseId: MOCK_PROTOCOL_PHASE_ID,
        type: "endometrial_thickness",
        value: 7.2,
        createdAt: MOCK_MONITORING_DATE,
        updatedAt: MOCK_MONITORING_DATE,
        updatedBy: null,
        source: "provider",
        verificationStatus: "verified",
        verifiedAt: null,
        verifiedBy: null,
        verificationNotes: null,
      },
    ],
  } as Phase,
];

const MOCK_RETRIEVAL_TRIGGER_SHOT: NonNullable<Phase["triggerShot"]> = {
  id: "00000000-0000-0000-0000-00000000a009",
  phaseId: MOCK_PROTOCOL_PHASE_ID,
  medicationId: null,
  catalogItemId: null,
  date: daysAgoISO(3),
  time: "21:00",
  dose: null,
  doseUnit: null,
  route: null,
  notes: null,
  createdAt: daysAgoISO(3),
  updatedAt: daysAgoISO(3),
  updatedBy: null,
  source: "patient",
  verificationStatus: "unverified",
  verifiedAt: null,
  verifiedBy: null,
  verificationNotes: null,
};

/** Egg retrieval started two days ago and has no result recorded yet. */
const MOCK_RETRIEVAL_PENDING_CYCLE_PHASES: Phase[] = [
  {
    ...MOCK_DEFAULT_CYCLE_PHASES[0]!,
  },
  {
    ...MOCK_DEFAULT_CYCLE_PHASES[1]!,
    endDate: daysAgoISO(4),
    triggerShot: MOCK_RETRIEVAL_TRIGGER_SHOT,
    ovaryFollicles: undefined,
    testResults: undefined,
    fertilityMetrics: undefined,
  },
  {
    id: "00000000-0000-0000-0000-00000000a008",
    cycleId: MOCK_CYCLE_ID,
    type: "egg_retrieval",
    startDate: daysAgoISO(2),
    startTime: "09:00",
    endDate: null,
    notes: null,
    createdAt: daysAgoISO(2),
    updatedAt: daysAgoISO(0),
    updatedBy: null,
    source: "patient",
    verificationStatus: "unverified",
    verifiedAt: null,
    verifiedBy: null,
    verificationNotes: null,
  } as Phase,
];

const MOCK_LUTEAL_PHASE_ID = "00000000-0000-0000-0000-00000000a007";

/**
 * Post-transfer two-week-wait phases. Every step from pre-treatment through
 * transfer is completed (endDate strictly after startDate); the luteal phase
 * is open with a startDate = transfer date (5 days ago) and endDate = beta
 * test date (5 days out). That shape mirrors what Cycle Detail's waiting view
 * expects, and lets `resolveCycleHeaderState` land on beta_scheduled.
 */
const WAITING_DAYS_AFTER_TRANSFER =
  DEV_WAIT_DAYS_AFTER_TRANSFER_OVERRIDE === null
    ? 5
    : DEV_WAIT_DAYS_AFTER_TRANSFER_OVERRIDE + 1;
const WAITING_BETA_TOTAL_DAYS =
  DEV_WAIT_DAYS_AFTER_TRANSFER_OVERRIDE === null ? 10 : 14;
const MOCK_WAITING_CYCLE_PHASES: Phase[] = [
  { type: "pre_treatment", startAgo: 30, endAgo: 26 },
  { type: "protocol", startAgo: 26, endAgo: 15 },
  { type: "egg_retrieval", startAgo: 12, endAgo: 11 },
  { type: "fertilization", startAgo: 11, endAgo: 10 },
  { type: "embryo_development", startAgo: 10, endAgo: 5 },
  { type: "transfer", startAgo: 5, endAgo: 4 },
  // Luteal open: startDate 5 days ago (transfer), endDate 5 days out (beta).
  {
    type: "luteal_phase",
    startAgo: WAITING_DAYS_AFTER_TRANSFER,
    endAgo: WAITING_DAYS_AFTER_TRANSFER - WAITING_BETA_TOTAL_DAYS,
  },
].map(
  ({ type, startAgo, endAgo }, index) =>
    ({
      id:
        type === "luteal_phase"
          ? MOCK_LUTEAL_PHASE_ID
          : `00000000-0000-0000-0000-00000000b00${index}`,
      cycleId: MOCK_CYCLE_ID,
      type,
      startDate: daysAgoISO(startAgo),
      startTime: null,
      endDate:
        type === "luteal_phase" &&
        DEV_ACTIVE_CYCLE_SCENARIO === "waiting_beta_pending"
          ? null
          : daysAgoISO(endAgo),
      notes: null,
      createdAt: daysAgoISO(startAgo),
      updatedAt: daysAgoISO(0),
      updatedBy: null,
      source: "patient",
      verificationStatus: "unverified",
      verifiedAt: null,
      verifiedBy: null,
      verificationNotes: null,
    }) as Phase,
);

const MOCK_WAITING_PHASES: Phase[] = [
  {
    id: "00000000-0000-0000-0000-00000000a003",
    cycleId: MOCK_CYCLE_ID,
    type: "transfer",
    startDate: daysAgoISO(3),
    startTime: null,
    endDate: daysAgoISO(2),
    notes: null,
    createdAt: daysAgoISO(3),
    updatedAt: daysAgoISO(3),
    updatedBy: null,
    source: "patient",
    verificationStatus: "unverified",
    verifiedAt: null,
    verifiedBy: null,
    verificationNotes: null,
  } as Phase,
  {
    id: "00000000-0000-0000-0000-00000000a004",
    cycleId: MOCK_CYCLE_ID,
    type: "luteal_phase",
    startDate: daysAgoISO(3),
    startTime: null,
    endDate: daysAgoISO(-11),
    notes: null,
    createdAt: daysAgoISO(3),
    updatedAt: daysAgoISO(0),
    updatedBy: null,
    source: "patient",
    verificationStatus: "unverified",
    verifiedAt: null,
    verifiedBy: null,
    verificationNotes: null,
  } as Phase,
];

export const MOCK_CYCLE_PHASES: Phase[] =
  DEV_ACTIVE_CYCLE_SCENARIO === "retrieval_pending"
    ? MOCK_RETRIEVAL_PENDING_CYCLE_PHASES
    : DEV_ACTIVE_CYCLE_SCENARIO === "waiting_beta" ||
        DEV_ACTIVE_CYCLE_SCENARIO === "waiting_beta_pending"
      ? MOCK_WAITING_CYCLE_PHASES
      : DEV_ACTIVE_CYCLE_SCENARIO === "waiting_phase"
        ? MOCK_WAITING_PHASES
        : MOCK_DEFAULT_CYCLE_PHASES;

export { MOCK_CYCLE_ID };
